/* RepoExpansionPanelRenderer.js */

"use strict";
const _ = require("lodash");
_.mixin(require("underscore.string").exports());
const PackageList = require("../packagelist/PackageListComponent");
const ExpandCollapseWidget = require("../../../widgets/expandcollapse/ExpandCollapseComponent");

class RepoExpansionPanelRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("repo-expansion-panel-gl");
        this._rootElem.component = this._model;

        this._packagesList = new PackageList();

        //Component Parts
        this._headerElem = undefined;
        this._repoTypeIconElem = undefined;
        this._repoNameElem = undefined;
        this._repoExpandCollapseComponent = undefined;
        this._expandedContentElem = undefined;
        this._contentElem = undefined;
        this._footerElem = undefined;

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();
        let shutDownProm = Promise.all([this._repoExpandCollapseComponent.shutdown(), this._packagesList.shutdown()]);
        this._rootElem.remove();
        this._rootElem = undefined;
        this._headerElem = undefined;
        this._repoTypeIconElem = undefined;
        this._repoNameElem = undefined;
        this._expandedContentElem = undefined;
        this._contentElem = undefined;
        this._footerElem = undefined;
        this._model = undefined;
        this._packagesList = undefined;
        return shutDownProm;
    }
    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("RepoReplaced", (this._onRepoReplaced = this._onRepoReplaced.bind(this)));
        this._model.on("RepoExpanded", (this._onRepoExpanded = this._onRepoExpanded.bind(this)));
        this._model.on("RepoCollapsed", (this._onRepoCollapsed = this._onRepoCollapsed.bind(this)));
        this._packagesList.on("LaunchEditor", (this._onLaunchEditor = this._onLaunchEditor.bind(this)));
    }
    _unregisterListeners() {
        this._model.removeListener("Shutdown", this._onShutdown);
        this._model.removeListener("RepoReplaced", this._onRepoReplaced);
        this._model.removeListener("RepoExpanded", this._onRepoExpanded);
        this._model.removeListener("RepoCollapsed", this._onRepoCollapsed);
        this._packagesList.removeListener("LaunchEditor", this._onLaunchEditor);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._renderHeader();
        this._renderExpandedContentContainer();
        this._renderCollapsedState();

        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._headerElem);
            this._rootElem.appendChild(this._expandedContentElem);
        }

        return Promise.resolve();
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onShutdown() {
        this._shutdown();
    }
    _onRepoReplaced() {
        this.render();
    }
    _onRepoExpanded() {
        this._expand();
    }
    _onRepoCollapsed() {
        this._collapse();
    }
    _onLaunchEditor(editorState, editorName, opts) {
        this._model.launchEditor(editorState, editorName, opts);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE HELPER METHODS */
    /*******************************************/
    _getRepoTypeIconElem() {
        let type = this._model.getType();
        let typeClass = _.underscored(type);
        let iconElem = document.createElement("base-icon-gl");
        switch (type) {
            case "DC":
                iconElem.dataset.icon = "slnman.dcrepo";
                iconElem.title = "This is a Distribution Center Repository.";
                break;
            case "FS":
                iconElem.dataset.icon = "slnman.filerepo";
                iconElem.title = "This is a local file based Package Repository.";
                break;
            case "DB":
                iconElem.dataset.icon = "slnman.filerepo";
                iconElem.title = "This is a local Package Repository.";
                break;
            default:
                //TODO should never get here.
                break;
        }
        iconElem.classList.add("repo_type_icon", typeClass, "not_actionable");

        return iconElem;
    }
    /*******************************************/
    /* END PRIVATE HELPER METHODS */
    /* START PRIVATE ACTION METHODS */
    /*******************************************/
    _expand() {
        this._repoExpandCollapseComponent.expand();
        this._renderCollapsedState();
    }
    _collapse() {
        this._repoExpandCollapseComponent.collapse();
        this._renderCollapsedState();
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _renderCollapsedState() {
        if (this._model.isCollapsed()) {
            this._expandedContentElem.classList.add("collapsed");
            this._expandedContentElem.classList.remove("expanded");
        } else {
            this._expandedContentElem.classList.remove("collapsed");
            this._expandedContentElem.classList.add("expanded");
        }
    }
    _renderHeader() {
        let repoName = this._model.getName();
        let repoDescription = this._model.getDescription();
        //Build the header structure
        if (!this._headerElem) {
            this._headerElem = document.createElement("expansion-panel-header-gl");
            this._headerElem.innerHTML = `
                <h1 class="repo_name"></h1>
            `;

            //Handle collapse/expand of the repo
            this._repoExpandCollapseComponent = new ExpandCollapseWidget();
            this._repoExpandCollapseComponent.renderer.rootElement.classList.add("repo_expander", "collapsed");
            this._headerElem.appendChild(this._repoExpandCollapseComponent.renderer.rootElement);
            this._repoExpandCollapseComponent.renderer.render();

            this._headerElem.addEventListener("click", (/*clkEvt*/) => {
                this._model.toggleCollapsed();
            });
        }
        //Update the pieces of the Header that can change if a repo exists
        if (this._model.repo) {
            //replace the current icon element
            this._repoTypeIconElem = this._headerElem.querySelector(".repo_type_icon");
            if (this._repoTypeIconElem) {
                this._repoTypeIconElem.remove();
            }
            this._repoTypeIconElem = this._getRepoTypeIconElem();
            this._headerElem.insertBefore(this._repoTypeIconElem, this._headerElem.firstChild);

            this._repoNameElem = this._headerElem.querySelector(".repo_name");
            this._repoNameElem.title = repoDescription;
            this._repoNameElem.textContent = repoName;
        }
    }
    _renderExpandedContentContainer() {
        if (!this._expandedContentElem) {
            this._expandedContentElem = document.createElement("expansion-panel-expanded-container-gl");

            //expansion_content
            this._renderContent();
            this._expandedContentElem.appendChild(this._contentElem);

            //expansion_footer
            this._renderFooter();
            this._expandedContentElem.appendChild(this._footerElem);
        }
    }
    _renderContent() {
        if (!this._contentElem) {
            this._contentElem = document.createElement("expansion-panel-expanded-content-gl");
            this._contentElem.appendChild(this._packagesList.renderer.rootElement);
        }

        let pkgs = this._model.getPackages();

        this._packagesList.setPackages(pkgs);
    }
    _renderFooter() {
        if (!this._footerElem) {
            this._footerElem = document.createElement("expansion-panel-footer-gl");
        }
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /*******************************************/
}
module.exports = RepoExpansionPanelRenderer;
